import { Component, OnInit, ViewChild, ElementRef, ViewEncapsulation, OnDestroy, DestroyRef, AfterViewInit } from '@angular/core';
import { LoginComponent, LoginInfo } from './login/login.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import { MatMenuTrigger } from '@angular/material/menu';
import { ContextService, ServerServiceConfig, LoginData, LayoutInterface } from './context.service';
import { Router, NavigationStart, ActivatedRoute } from '@angular/router';
import { ServerLoginService } from './server-login.service';
import { Subscription } from 'rxjs';
import { BreakpointObserver } from '@angular/cdk/layout';
import { MatToolbar } from '@angular/material/toolbar';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';

import tinycolor from 'tinycolor2';

/** This is the welcome page that shows when we are not logged in and may not have connected */

@Component({
  selector: 'mutara-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  favIcon: HTMLLinkElement | null = document.querySelector("#appFavIcon");
  favIcon16: HTMLLinkElement | null = document.querySelector("#appFavIcon16");
  favIcon32: HTMLLinkElement | null = document.querySelector("#appFavIcon32");
  appTitle: HTMLTitleElement | null = document.querySelector("#appTitle");
  @ViewChild(MatToolbar) toolbar: MatToolbar | undefined;
  @ViewChild("navTrigger") accountNav: MatMenuTrigger | undefined;
  /*
    context = {
      chat: []
    }
    chatTimer: any;
    */
  today: number = Date.now();
  progress: number = 0;
  message: string | undefined;

  // This is the user data
  loginData: LoginData | undefined;
  appConfig: ServerServiceConfig | undefined;
  currentLayout: LayoutInterface | undefined;
  layouts: LayoutInterface[] = [];

  isEmbed: boolean = false;
  showProgress: boolean = false;

  pageTitle: string | null = null;
  configDesc: string | null = null;
  isVertical: boolean = false;
  editorVisible: boolean = false;
  subscriptions: Subscription[] = [];
  oldMessages: string[] = []

  // Dialogs login and chat
  private loginDialog: MatDialogRef<LoginComponent> | undefined | null;

  private colorMap: any = {
    '50': { dark: false, value: 12 },
    '100': { dark: false, value: 30 },
    '200': { dark: false, value: 50 },
    '300': { dark: false, value: 70 },
    '400': { dark: false, value: 85 },
    '500': { dark: false, value: 100 },
    '600': { dark: true, value: 87 },
    '700': { dark: true, value: 70 },
    '800': { dark: true, value: 54 },
    '900': { dark: true, value: 25 },
    'A100': { dark: true, value: 15, saturation: 80, light: 65 },
    'A200': { dark: true, value: 15, saturation: 80, light: 55 },
    'A400': { dark: true, value: 15, saturation: 100, light: 45 },
    'A700': { dark: true, value: 15, saturation: 100, light: 40 }
  };

  constructor(
    private httpConnection: HttpClient,
    public router: Router,
    private actRoute: ActivatedRoute,
    private contextSettings: ContextService,
    private element: ElementRef,
    private dialog: MatDialog,
    private motd: MatSnackBar,
    private breakpointObs: BreakpointObserver
  ) {
    this.breakpointObs.observe(`(max-width: 950px)`)
      .subscribe(state => {
        this.contextSettings.nextMessage("orientation", state.matches);
      });
    // This is where we load up the theme color palettes and setup instructions etc.
    this.subscriptions.push(this.contextSettings.subscribeTo("applicationInfo", (config: ServerServiceConfig) => {
      if (config === null) {
        console.error('No config yet!! Are we connecting to the remote services or have they crashed?');
        return;
      }
      if (this.appConfig === undefined || !(this.appConfig.serviceVersion == config.serviceVersion && this.appConfig.serviceUrl == config.serviceUrl)) {
        if (!this.appConfig?.production) {
          this.manageFakeSubmit();
          this.contextSettings.addNewMessage('FAKEPAYMENT', false);
        }
        this.appConfig = config;
        if ( !!this.appConfig?.notes ) {
          this.oldMessages.push(this.message = this.appConfig?.notes);
        }
        this.configDesc = config?.description;

        this.element.nativeElement.style.setProperty('--registration-image', `url(${this.appConfig?.registrationImage})`);
        this.element.nativeElement.style.setProperty('--toolbar-background', `url(/assets/${this.appConfig?.themeDir}/toolbar-background.png)`);
        /// The following values are from the index.html wrapper of the app and need the reset once the config is loaded
        if (!!this.appTitle) {
          this.appTitle.text = this.getPageTitle();
        }
        if (!!this.favIcon) {
          this.favIcon.href = `/assets/${config.themeDir}/favicon.ico`;
        }
        this.buildColorMaps(config);
        /*
        this.favIcon16.href = `/assets/${config.themeDir}/favicon-16x16.png`;
        this.favIcon32.href = `/assets/${config.themeDir}/favicon-32x32.png`;
        */

        this.contextSettings.loadPropertyVariables(config.theme, this.element.nativeElement.style);
      }
    }));
  }

  /**
   * Here we load from local storage to allow this user to reconnect to the service server
   */
  ngOnInit() {
    this.subscriptions.push(this.actRoute.queryParams.subscribe(params => {
      this.isEmbed = (params.embedded == 'true');
    }));
  
    // Reset the layout list
    this.subscriptions.push(this.contextSettings.subscribeTo("logout", () => {
      this.layouts = [];
      this.pageTitle = null;
      if (!!this.appTitle) { this.appTitle.text = this.getPageTitle(); }

    }));
    // This is to catch any application changes (currently doesn't happen except once)
    this.subscriptions.push(this.contextSettings.subscribeTo("loginRequest", (popup: boolean) => {
      if (popup) {
        this.createLoginDialog();
      }
    }));
    // This is the receiver for the login data
    this.subscriptions.push(this.contextSettings.subscribeTo("loginData", (login: LoginData) => {
      this.loginData = login;
      this.contextSettings.setLayoutId(login?.defaultLayout);
    }));

    this.subscriptions.push(this.contextSettings.subscribeTo("layoutIdentity", (layout: LayoutInterface) => {
      this.currentLayout = layout;
      this.pageTitle = this.contextSettings.expandVariables(layout?.layoutKey ?? this.configDesc, this.appConfig?.info);
      if (!!this.appTitle) {
        this.appTitle.text = this.getPageTitle();
      }
    }));

    this.subscriptions.push(this.contextSettings.subscribeTo("orientation", (isVertical: boolean) => {
      this.isVertical = isVertical;
    }));

    
  }

  /**
   * These are the ones that need to wait until the page is DOM'd
   */
  ngAfterViewInit(): void {
    this.subscriptions.push(this.httpConnection.get('$service/motd').subscribe((result: any) => {
      if (!!result && result.length > 0) {
        this.contextSettings.changeMessage(result.reduce((acc: any, messageObj: any) => `${acc} ${messageObj.message}\n`, '') );
      }
    }));
    this.subscriptions.push(this.contextSettings.subscribeTo("showProgressValue", (visible: boolean) => {
      this.showProgress = visible;
    }));

    this.subscriptions.push(this.contextSettings.subscribeTo("messageBar",(messageText : string) => {
      if ( (messageText ?? '') != '' && !this.oldMessages.includes( messageText)) {
        this.oldMessages.push( this.message = messageText);
      }
    }));
  }

  /**
   * Clean up the subscriptions
   */
  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscriptions = [];
  }

  get isLoggedIn() {
    return this.loginData && (this.loginData?.username ?? '') != '';
  }

  get currentRoute(): string {
    return this.router.url.split('/')[1];
  }

  manageFakeSubmit() {
    this.contextSettings.addNewMessage('FAKESUBMIT', false);
    this.subscriptions.push(this.contextSettings.subscribeTo('FAKESUBMIT', (testSubmit: boolean) => {
      if (testSubmit && this.isLoggedIn) {
        this.contextSettings.nextMessage('formComplete', {valid: true});
      }
    }));
  }

  /// Derive the colors for the palette variables..... Angular2 
  __colorAdjust(tc: | any) {
    return new tinycolor({
      r: Math.floor(Math.pow(tc._r, 2) / 255),
      b: Math.floor(Math.pow(tc._b, 2) / 255),
      g: Math.floor(Math.pow(tc._g, 2) / 255)
    });
  }

  buildColorMaps(config: any) {
    ['primary', 'accent', 'warn', 'success', 'neutral', 'indeterminate'].forEach(type => {
      try {
        let name = `--${type}-color`, value = config.theme?.[name];
        if (!!value) {
          let color = new tinycolor(value),
            light = new tinycolor('#ffffff'),
            black = new tinycolor('#000000'),
            dark = new tinycolor(this.__colorAdjust(color)),
            triad = color.tetrad()[3];
          // Walk the map and build the full color profile as used in the application
          Object.keys(this.colorMap).forEach(level => {
            if (!config.theme[`--${type}-${level}`]) {
              let defn = this.colorMap[level],
                newColor = tinycolor.mix(defn.dark ? dark : light,
                  !defn.saturation ? color : triad,
                  defn.value);//.saturate(defn.saturation ?? 100).lighten(defn.light ?? 0);
              if (!!defn.saturation) {
                newColor = newColor.saturate(defn.saturation);
              }
              if (!!defn.light) {
                newColor = newColor.lighten(defn.light);
              }

              config.theme[`--${type}-${level}`] = '#' + newColor.toHex();
              config.theme[`--${type.substring(0, 1)}-contrast-${level}`] = '#' + (newColor.isDark() ? light : black).toHex();
            }
          });
        }
      } catch (err) {
        console.log('No Color Maps for %O\n%O', config.description, err);
      }
    });

  }
  /**
   * opens a login dialog and sets up the processing subscription
   */
  createLoginDialog() {
    if (!this.loginDialog) {
      this.layouts = [];
      this.loginDialog = this.dialog.open(LoginComponent,
        {
          disableClose: false,
          autoFocus: true,
          // panelClass: 'custom-dialog',
          width: 'auto',
          height: 'auto',
          hasBackdrop: true,
          data: {},
          role: 'dialog',
          ariaLabelledBy: 'modal-title',
          id: 'modal-container-' + Date.now()
        });

      this.subscriptions.push(this.loginDialog.componentInstance.roleOptionData.asObservable().subscribe({
        next: (opts) => {
          this.layouts = opts;
        }
      }));

      this.subscriptions.push(this.loginDialog.afterClosed().subscribe({
        next: (data) => {
          setTimeout(() => this.processLogin(data));
        }
      }));
    }
  }

  /**
   * Send the data to the server for authentication. If successful, we recieve context and user information
   * @param loginData
   */
  processLogin(loginData: any) {
    if (this.loginDialog) {
      this.loginDialog = null;
    }
    // This checks the data from the dialog to see what needs to happen
    if (!!loginData?.user && !!loginData?.pwd) {
      // here we have several options, 

      // Login, Login with Role, Change Password
      const service = !!loginData.forgotten ? '$service/change-password': '$service';

      this.subscriptions.push(this.httpConnection.post(service, loginData).subscribe({
        error: (err: any)  => {
          console.error(err.message);
          this.createLoginDialog();
        },
        next: (data: any) => {
          if ((data?.type ?? 0)== 0) {
            this.layouts = [];
          }
        }
      }));
    }
  }

  /**
   * clicked the button to logout the user. This needs to reset things
   * @param $event
   */
  logoutUser($event: Event) {
    console.log('Logout Button Press %O', $event);
    this.accountNav?.closeMenu();

    this.contextSettings.nextMessage('logout', true);
  }

  login($event: Event) {
    if (!!this.loginData && !this.loginData.username && !!this.accountNav) {
      $event.preventDefault();
      this.accountNav?.closeMenu();
      this.createLoginDialog();
    }
    return !this.loginData?.username;
  }

  getPageTitle(): string {
    return this.pageTitle ?? this.configDesc ?? 'Mutara Application';
  }

  showMOTD() {
    if ( this.oldMessages.length > 0 ) {
      this.motd.open(this.oldMessages.reverse().join('\n\n'), 'OK', {duration: 30000, panelClass : 'snack-bar'});
    }
  }

  switchLayout(layout: any) {
    switch (layout.value) {
      case 'editor':
        this.contextSettings.activateEditor((layout.active = !layout.active));
        break;
      case 'reload':
        this.contextSettings.reloadServices(ServerLoginService.configService);
        break;
      default:
        this.contextSettings.setLayoutId(this.contextSettings.findLayout(layout.value));
        break;
    }
  }
  /**
   * Recalc the deploy time
   */
  getDeployTime() {
    return !!this.appConfig?.serviceVersion ? new Date(parseInt((this.appConfig?.serviceVersion ?? '').split('.')?.pop() ?? '0') * 3600000).toString() : new Date(0);
  }
  dumpForm() {
    this.contextSettings.dumpFormData();
  }
  toggleEditor() {
    this.contextSettings.nextMessage('formEditor', this.editorVisible = !this.editorVisible);
  }
  fakeSubmit() {
    if (!!this.login) {
      this.contextSettings.nextMessage('FAKESUBMIT', true);
    }
  }
  fakeRelaunch(success: boolean) {
    if (!!this.login) {

      this.contextSettings.nextMessage('FAKEPAYMENT', success);
    }
  }

  jumpTo( ) {
    if ( this.currentLayout ) {
      this.contextSettings.setLayoutId( Object.assign({...this.currentLayout}, {tab: 'review'} ) );
    }
  }
  skipToMainContentClick(event: Event) {
    event.preventDefault();
    let thisElem = document.getElementById("content") as HTMLAnchorElement;
    thisElem.focus();
    return;
  }

  get flairImage() {
    return '/assets/' + (this.appConfig?.themeDir ?? 'mutara') + (!!this.appConfig?.flairOverHome ? '/home-image.png' : '/flair.png');
  }
  get homeImage() {
    return '/assets/' + (this.appConfig?.themeDir ?? 'mutara') + (!!this.appConfig?.flairOverHome ? '/flair.png' : '/home-image.png');
  }
}